import { estiloDoItem, urlDoBalao } from "./balao-do-estilo";
import { ehCenarioPronto, urlDoCenarioPronto } from "./cenarios-prontos";

/**
 * QUAIS ITENS ENTRAM NA PÁGINA, E COM QUE IMAGEM.
 *
 * Isto saiu de dentro de `Layer.fromFirestoreLayerObj` para poder ser TESTADO. E a razão é
 * concreta: `layer.class` importa a fábrica de itens, que importa as classes do Konva, que
 * importam `konva/cmj/index-node`, que exige o pacote nativo `canvas`. Sob o jest, a cadeia
 * inteira não carrega.
 *
 * O que precisa de teste aqui não é o Konva: é a DECISÃO sobre a lista. "Que itens sobrevivem ao
 * carregamento" é lógica pura, e foi justamente onde morava o defeito.
 *
 * ── O DEFEITO QUE ESTE ARQUIVO EXISTE PARA NÃO REPETIR ──────────────────────────────────────
 *
 * A versão anterior filtrava:
 *
 *     obj.items.filter((item) => !item.imgId || item.imgId in imgs)
 *
 * **Todo item cuja imagem ainda não tinha chegado era descartado, em silêncio.** E as imagens
 * chegam por uma escuta SEPARADA da dos quadrinhos (`global-state-context`, linhas 134 e 157), em
 * ordem não garantida — então "ainda não chegou" é o estado normal do primeiro carregamento.
 *
 * O sintoma visível é o `T2.47` (*"eu abro a primeira, ele aparece assim, aí eu clico em cima de
 * novo"*), que ele marcou *"pra ontem"* (`T2.48`). O invisível é pior: `Comic.save()` grava as
 * páginas em memória, então abrir antes das imagens e salvar apagava os itens do documento.
 *
 * A correção é não descartar. Item sem imagem nasce com URL vazia — e `DraggableItem` já tratava
 * isso (`if (!imgUrl) this.isLoaded = true`), o que prova que o código base esperava imagem
 * ausente e o filtro o contradizia.
 */

/** O mínimo que este módulo precisa saber de um item. Evita importar a cadeia do Konva. */
export interface ItemComImagem {
  imgId?: string;
}

/** E o mínimo que ele precisa saber do catálogo de imagens. */
export interface ImagemDoCatalogo {
  url?: string;
}

/**
 * Para cada item da página, o item e a URL da imagem dele.
 *
 * **Nunca descarta.** A lista que sai tem o mesmo tamanho e a mesma ORDEM da que entra — e a
 * ordem é informação: em quadrinho, quem está na frente de quem é o empilhamento. Remover do meio
 * muda a pilha, e o resultado é arte por cima de balão.
 */
/**
 * É um balão que pode receber forma desenhada?
 *
 * Só `textBubbles`: `figures` e `characters` sem imagem continuam nascendo vazios de propósito —
 * o cabeçalho acima explica por quê, e inventar uma forma para eles poria na página da criança um
 * elemento que ela não pediu.
 */
function ehBalaoComEstilo(item: ItemComImagem): item is ItemComImagem & { estilo?: unknown; id?: unknown } {
  return (item as { type?: string }).type === "textBubbles";
}

export function itensComSuasImagens<T extends ItemComImagem>(
  itens: T[],
  imgs: Record<string, ImagemDoCatalogo>
): { item: T; url: string }[] {
  return itens.map((item) => {
    // `?? ""` e não `||`: uma URL vazia gravada de propósito continua vazia, e a diferença entre
    // "não achei" e "está vazio" não importa para quem desenha — mas importa para quem lê este
    // código depois.
    // T11.6 · o cenário pronto mora no produto, não no catálogo da conta. Sem esta linha ele
    // entrava na página, era salvo, e SUMIA ao reabrir (medido em 06/10, no ambiente de teste).
    const doCatalogo = ehCenarioPronto(item.imgId)
      ? urlDoCenarioPronto(item.imgId)
      : (item.imgId ? imgs[item.imgId]?.url : undefined) ?? "";

    // ── BALÃO SEM IMAGEM GANHA A FORMA DESENHADA (T6.5) ─────────────────────────────────────
    //
    // Ele: *"Ao salvar, saiu sem formatação de quadrinho, de fala. Tá só um texto."*
    //
    // O `TextBubble` desenha a forma do balão como IMAGEM. Um balão vindo do roteirizador não tem
    // imagem — o acervo é por conta e o roteirizador não sobe balão nenhum —, então a forma não
    // aparecia e sobrava só o texto, solto no meio do desenho.
    //
    // A forma agora vem de `balao-do-estilo.ts`, em SVG, pelo estilo que o próprio roteirizador
    // gravou. E entra por AQUI e não no `TextBubble`: assim o Konva não muda uma linha, e a regra
    // fica num módulo que o jest carrega — o `TextBubble` importa a cadeia do Konva, que não sobe
    // sob o jest sem o pacote nativo `canvas`. É a mesma razão pela qual a decisão da LISTA mora
    // neste arquivo, e está no cabeçalho acima.
    //
    // E o balão que TEM imagem continua com a dela: a criança que escolheu um balão no editor
    // escolheu aquele, e trocá-lo por um desenhado seria desfazer o trabalho dela.
    if (!doCatalogo && ehBalaoComEstilo(item)) {
      // T20 · `estiloDoItem` e não `estiloOuFala(item.estilo)`: o balão salvo antes do conserto
      // não tem `estilo`, e ler só ele transformava todo recordatório em fala ao reabrir.
      return { item, url: urlDoBalao(estiloDoItem(item)) };
    }

    return { item, url: doCatalogo };
  });
}

/**
 * Quantos itens da página estão SEM a imagem no momento.
 *
 * Não muda comportamento nenhum: serve para medir. Enquanto este número for maior que zero, a
 * página está desenhando incompleta — e saber disso é o que permite, um dia, a tela dizer
 * "carregando os desenhos" em vez de mostrar a folha pela metade em silêncio.
 */
export function quantosSemImagem<T extends ItemComImagem>(
  itens: T[],
  imgs: Record<string, ImagemDoCatalogo>
): number {
  return itens.filter((i) => i.imgId && !imgs[i.imgId]?.url).length;
}
